import type { StopAnchorWire, TripDelayWire } from "../../../../types/dtos/realtime-api/realtime.dto";
import { buildRealtimeTransitPathMarkerPresentationFingerprint } from "./realtimeTransitPath.configuration";

/** Overnight GTFS trips are represented with times above 24h. */
const SERVICE_DAY_ROLLOVER_WINDOW_S = 6 * 3600;

/**
 * Returns the current local time as seconds since midnight.
 *
 * @returns Seconds since local midnight.
 */
export function secondsSinceMidnight(): number {
    const now = new Date();

    return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
}

/**
 * Aligns "now" with the service-day encoding used by the realtime schedule.
 *
 * @param stops Trip stop anchors.
 * @returns Comparable seconds-since-midnight for schedule comparison.
 */
export function getServiceDaySecondsForSchedule( stops: readonly StopAnchorWire[] ): number {
    const localSeconds = secondsSinceMidnight();
    const hasOvernightSchedule = stops.some( ( stop ) => stop.arr > 86400 || stop.dep > 86400 );
    const isEarlyMorning = localSeconds < SERVICE_DAY_ROLLOVER_WINDOW_S;

    if ( hasOvernightSchedule && isEarlyMorning ) {
        return localSeconds + 86400;
    }

    return localSeconds;
}

/**
 * Fingerprint of delay payload for cheap inequality checks.
 *
 * @param delay Optional trip delay wire.
 * @returns Stable fingerprint string.
 */
export function tripDelayFingerprint( delay: TripDelayWire | undefined ): string {
    if ( delay === undefined ) {
        return "";
    }

    return JSON.stringify( {
        "global": delay.global,
        "stops":  delay.stops,
    } );
}

/**
 * Builds a fingerprint for static visual state (delay, color, theme, marker presentation).
 *
 * @param delay Optional trip delay wire.
 * @param colorHex Route color hex.
 * @param isDarkUi Whether dark UI is active.
 * @returns Fingerprint string.
 */
export function buildRealtimeTransitStaticStateFingerprint(
    delay: TripDelayWire | undefined,
    colorHex: string,
    isDarkUi: boolean,
): string {
    return `${ tripDelayFingerprint( delay ) }|${ colorHex }|${ String( isDarkUi ) }|${ buildRealtimeTransitPathMarkerPresentationFingerprint() }`;
}

/**
 * Resolves the schedule-based next stop index using **predicted arrival** times.
 *
 * @remarks
 * This uses `stop.arr + delay` (arrival), then {@link resolveEffectiveNextStopIndex}
 * corrects with live GPS on the shape. Do **not** substitute
 * `findNextStopIndexDelayAware` from `MapItineraryTools` here — that helper uses
 * predicted **departure** (`stop.dep + delay`), which is a different product
 * semantics (dwell at stops, itinerary tooling). Unify only with an explicit
 * product decision.
 *
 * @param stops Ordered vehicle-trip stops.
 * @param getDelayForStopSequence Delay lookup for a stop sequence.
 * @param nowSeconds Current service-day seconds.
 * @returns Index of the next stop, or -1 when the trip has finished for today.
 */
export function resolveArrivalBasedNextStopIndex(
    stops: readonly StopAnchorWire[],
    getDelayForStopSequence: ( stopSeq: number ) => number,
    nowSeconds: number,
): number {
    return stops.findIndex( ( stop ) => {
        const predictedArrivalSeconds = stop.arr + getDelayForStopSequence( stop.seq );

        return predictedArrivalSeconds >= nowSeconds;
    } );
}
